import api from './api';

export const isFallbackDocument = (document) => {
  const id = document?._id;
  return typeof id === 'string' && (
    id.startsWith('doc-') ||
    id.startsWith('practicing-') ||
    id.startsWith('resume-')
  );
};

// Uploaded files are no longer served statically from /uploads — that mount was
// unauthenticated and its filename fallback reached across supplier folders.
// Everything now goes through GET /api/files/:supplierId/:fileName, which
// checks ownership, so these URLs must be fetched with the api client (which
// attaches the bearer token) rather than a bare fetch().
//
// Returns a path relative to the api client's baseURL, e.g. "/files/<id>/<name>".
export const buildUploadUrl = (filePath, supplierId) => {
  if (!filePath) return null;

  // Reduce whatever shape we were given — absolute URL, stored disk path, or
  // bare filename — down to a supplier directory and a file name.
  let candidate = filePath;

  if (/^https?:\/\//i.test(filePath)) {
    try {
      candidate = new URL(filePath).pathname;
    } catch {
      return null;
    }
  }

  const normalizedPath = String(candidate).replace(/\\/g, '/').replace(/^\.\//, '');
  const segments = normalizedPath.split('/').filter(Boolean);
  const fileName = segments[segments.length - 1];
  if (!fileName) return null;

  // A stored path like ".../uploads/<supplierId>/<file>" carries its own owner.
  const uploadsIndex = segments.lastIndexOf('uploads');
  const ownerFromPath = uploadsIndex >= 0 && segments.length - uploadsIndex === 3
    ? segments[uploadsIndex + 1]
    : null;

  const owner = ownerFromPath || supplierId;
  if (!owner) return null;

  return `/files/${encodeURIComponent(owner)}/${encodeURIComponent(fileName)}`;
};

export const fetchDocumentBlobUrl = async (documentId) => {
  const response = await api.get(`/documents/${documentId}/download`, {
    responseType: 'blob'
  });

  return window.URL.createObjectURL(new Blob([response.data], {
    type: response.headers['content-type'] || 'application/octet-stream'
  }));
};

// `path` comes from buildUploadUrl and is relative to the api baseURL, so the
// request carries the Authorization header. A bare fetch() would now 401.
export const fetchFileBlobUrl = async (path) => {
  if (!path) throw new Error('File is not available');

  const response = await api.get(path, { responseType: 'blob' });
  return window.URL.createObjectURL(new Blob([response.data], {
    type: response.headers['content-type'] || 'application/octet-stream'
  }));
};

// window.open() on a /files URL would send no Authorization header and 401.
// Fetch it as a blob and hand the browser a local object URL instead.
export const downloadFileFromPath = async (path, fileName) => {
  const blobUrl = await fetchFileBlobUrl(path);
  const link = window.document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'document';
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const downloadDocument = async (document) => {
  const blobUrl = await fetchDocumentBlobUrl(document._id);
  const link = window.document.createElement('a');
  link.href = blobUrl;
  link.download = document.originalName || document.fileName || 'document';
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// Betika's own blank forms, behind `protect` like everything else under /api.
// The download was an <a href> straight at the endpoint, which the browser
// navigates to without an Authorization header — so every form came back 401.
// Same reason /files and /documents are fetched as blobs above.
//
// `fileName` comes from the template listing rather than the response's
// Content-Disposition: the API is a different origin in most deployments and
// that header is not CORS-safelisted, so it would not be readable here.
export const downloadTemplate = async (key, fileName) => {
  const response = await api.get(`/templates/${encodeURIComponent(key)}`, {
    responseType: 'blob'
  });

  const blobUrl = window.URL.createObjectURL(new Blob([response.data], {
    type: response.headers['content-type'] || 'application/pdf'
  }));
  const link = window.document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || `${key}.pdf`;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};
